// Global Instructions Rule Applied!
import React, { useCallback, useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Card, List, Typography, Button } from 'antd'
import { fetchFundInvestorsFiltered, fetchFundInvestorsSummary } from '../../../../app_modules/funds/services/fundsService.ts'
import { getAccessToken } from '../../../auth/authService'
import { store } from '../../../../state_manager/store.ts'
import { detailPath, avatarInitials } from '../lib/routes.ts'
import { text } from '../lib/format.ts'
import { compactMoneyByCode } from '../lib/money.ts'
import { RecordRow } from '../components/RecordRow.tsx'
import { NormalizedRow } from '../lib/listDescriptors.ts'
import { LoadingState } from '../components/ui.tsx'

/**
 * Per-fund investors (commitments) — drilled into from the FundScreen capital summary.
 * Reuses the existing /funds/:id/investors endpoints (summary header + filtered grid) and
 * renders each commitment with the shared RecordRow card (commitment in compact units, like
 * the other mobile lists). Tapping a row opens the parent account.
 */
export function FundInvestorsScreen(): React.JSX.Element {
  const { id } = useParams()
  const navigate = useNavigate()
  const currencies = useSelector((s: any) => s?.globalConfigs?.currencies)

  const [rows, setRows] = useState<any[]>([])
  const [summary, setSummary] = useState<any | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setError(null)
    const token = getAccessToken(store.getState().auth)
    const [listRes, sumRes] = await Promise.all([
      fetchFundInvestorsFiltered(id, [], [], 1, 200, token),
      fetchFundInvestorsSummary(id, token)
    ])
    setRows(Array.isArray(listRes.data) ? listRes.data : [])
    setSummary(sumRes.data ?? null)
    setError(listRes.error || null)
    setLoading(false)
  }, [id])

  useEffect(() => {
    void load()
  }, [load])

  const toRow = useCallback(
    (row: any): NormalizedRow => {
      const code = typeof row.currency_code === 'string' ? row.currency_code : null
      const name = row.investors__name || row.accounts__name
      return {
        id: String(row.fund_commitments__id ?? row.investors__id ?? name ?? ''),
        module: 'companies',
        title: text(name),
        subtitle: row.investor_classifications__label ? String(row.investor_classifications__label) : undefined,
        avatarText: avatarInitials(name),
        tagText: row.fund_commitment_statuses__label ? String(row.fund_commitment_statuses__label) : undefined,
        stats:
          row.fund_commitments__amount != null
            ? [{ label: 'Commitment', value: compactMoneyByCode(row.fund_commitments__amount, code, currencies) }]
            : undefined
      }
    },
    [currencies]
  )

  if (loading && rows.length === 0) return <LoadingState label="Loading investors…" />

  return (
    <div style={{ padding: 12 }}>
      {summary ? (
        <Card size="small" style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>Committed</div>
              <div style={{ fontWeight: 600 }}>{text(summary.funds__committed_capital_fmt)}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>Investors</div>
              <div style={{ fontWeight: 600 }}>{text(summary.no_investors_fmt)}</div>
            </div>
          </div>
        </Card>
      ) : null}

      {error ? (
        <div style={{ padding: '4px 0' }}>
          <Typography.Text type="secondary">Couldn't load investors.</Typography.Text>
          <div style={{ marginTop: 8 }}>
            <Button size="small" onClick={() => void load()}>
              Retry
            </Button>
          </div>
        </div>
      ) : rows.length === 0 ? (
        <Typography.Text type="secondary">No investors.</Typography.Text>
      ) : (
        <List
          size="small"
          dataSource={rows}
          rowKey={(row: any) => String(row.fund_commitments__id ?? row.investors__id ?? '')}
          renderItem={(row: any) => (
            <RecordRow
              item={toRow(row)}
              onClick={row.accounts__id ? () => navigate(detailPath('companies', row.accounts__id)) : undefined}
            />
          )}
        />
      )}
    </div>
  )
}

export default FundInvestorsScreen
